import { FormInstance } from 'antd';
import React, { FC, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Account } from 'api/account';
import { CostRate, CostRateSourceType } from 'api/cost-rates';
import { Checkbox } from 'components/atoms/checkbox';
import { currencyRenderer } from 'components/atoms/currency-renderer';
import { Form } from 'components/atoms/form';
import { InputNumber } from 'components/atoms/input';
import { BodyShort01 } from 'components/styles';
import { PermissionsContext } from 'contexts/permissions';
import { useTranslation } from 'hooks/use-translation';
import { CostRateFormProps } from '.';

const StyledCheckbox = styled(Checkbox)`
  ${BodyShort01};
  display: flex;
  align-items: center;
`;

interface Props {
  form: FormInstance<CostRateFormProps>;
  account: Account;
  costRate?: CostRate;
  fallbackCostRate?: CostRate;
  sourceType: CostRateSourceType;
  checkCanSubmit: () => void;
}

export const CostRateForm: FC<Props> = ({
  form,
  account,
  costRate,
  fallbackCostRate,
  sourceType,
  checkCanSubmit,
}) => {
  const { canWrite } = useContext(PermissionsContext);

  const inputLabel = useTranslation('settings.cost_rates.drawer.input.label');
  const inputRequired = useTranslation('input.required');
  const inputPlaceholder = useTranslation(
    'settings.cost_rates.drawer.input.placeholder',
  );
  const inputHelp = useTranslation('settings.cost_rates.drawer.input.help');

  const [isInputDisabled, setIsInputDisabled] = useState(
    !canWrite ||
      (sourceType === CostRateSourceType.User &&
        costRate?.hourlyCostRate == null),
  );

  const initialFormValues = useMemo(
    () => ({
      hourlyCostRate:
        (costRate?.hourlyCostRate == null
          ? fallbackCostRate?.hourlyCostRate
          : costRate?.hourlyCostRate) ?? 0,
      clearCostRate: costRate?.hourlyCostRate == null,
    }),
    [costRate?.hourlyCostRate, fallbackCostRate?.hourlyCostRate],
  );

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initialFormValues}
      onValuesChange={(_, values) => {
        const { clearCostRate, hourlyCostRate } = values as CostRateFormProps;

        checkCanSubmit();
        setIsInputDisabled(clearCostRate);
        form.setFieldsValue({
          hourlyCostRate:
            (clearCostRate
              ? fallbackCostRate?.hourlyCostRate
              : hourlyCostRate ?? costRate?.hourlyCostRate) ?? 0,
        });
      }}
    >
      <Form.Item
        label={inputLabel}
        name="hourlyCostRate"
        help={inputHelp}
        rules={[
          {
            required: true,
            message: inputRequired,
          },
        ]}
        shouldUpdate
      >
        <InputNumber
          disabled={isInputDisabled}
          placeholder={inputPlaceholder}
          autoFocus={
            sourceType === CostRateSourceType.User
              ? costRate?.hourlyCostRate != null
              : true
          }
          min={0}
          formatter={(value) =>
            `${account.currency.currencySymbol} ${value}`.replace(
              /\B(?=(\d{3})+(?!\d))/g,
              ',',
            )
          }
          onPressEnter={({ currentTarget }) => currentTarget.blur()}
        />
      </Form.Item>
      {sourceType === CostRateSourceType.User && fallbackCostRate != null && (
        <Form.Item name="clearCostRate" valuePropName="checked" shouldUpdate>
          <StyledCheckbox>
            <FormattedMessage
              id="settings.cost_rates.drawer.clear_cost_rate"
              values={{
                type:
                  sourceType === CostRateSourceType.User ? 'role' : 'account',
                value: currencyRenderer(
                  fallbackCostRate.hourlyCostRate ?? 0,
                  account.currency.currencyCode,
                ),
                notSet: fallbackCostRate.hourlyCostRate == null,
              }}
            />
          </StyledCheckbox>
        </Form.Item>
      )}
    </Form>
  );
};
